import { useCallback, useMemo } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import useCategoryProfileLaunches, {
    CategoryProfileLaunches as CategoryProfileLaunchesData,
    EMPTY_CATEGORY_PROFILE_LAUNCHES,
} from "@apiHooks/useCategoryProfileLaunches";
import { useHomeCommonData } from "@apiHooks/useHomeCommonData";
import { ID } from "@models";
import { requestTrialByFeature } from "@actions/page";
import CategoryProfileSectionTitle from "@pages/CategoryProfile/components/CategoryProfileSectionTitle/CategoryProfileSectionTitle";
import CategoryProfileCard from "@pages/CategoryProfile/components/CategoryProfileCard/CategoryProfileCard";
import CategoryProfileSubtitle from "@pages/CategoryProfile/components/CategoryProfileSubtitle/CategoryProfileSubtitle";
import AccessLink from "@components/AccessLink/AccessLink";
import { CategoryProfileSectionComponentProps } from "@pages/CategoryProfile/CategoryProfile.constants";
import LaunchesActivityChart from "./components/LaunchesActivityChart/LaunchesActivityChart";
import LaunchesRankingList from "./components/LaunchesRankingList/LaunchesRankingList";
import RecentlyLaunchedCarousel from "./components/RecentlyLaunchedCarousel/RecentlyLaunchedCarousel";
import { buildLaunchesChainTooltip, LAUNCHES_FF_TOOLTIP } from "./Launches.constants";
import "./Launches.styles.scss";
import { useParams } from "react-router-dom";
import http from "@core/fetch";

function hasLaunchesData(data: CategoryProfileLaunchesData): boolean {
    return (
        data.activity.bySeason.length > 0 ||
        data.activity.byMonth.length > 0 ||
        data.topFoodsFlavors.length > 0 ||
        data.topChains.length > 0 ||
        data.recentItems.length > 0 ||
        data.topItems.length > 0
    );
}

type LaunchesSectionData = CategoryProfileLaunchesData & { lrCategoryIds: number[] };

export const useLaunchesSectionData = (categoryId: ID, lrCategoryIds?: number[] | null) => {
    const { data, isLoading } = useCategoryProfileLaunches(categoryId);
    return {
        hasData: hasLaunchesData(data),
        isLoading,
        data: { ...data, lrCategoryIds: lrCategoryIds ?? [] },
    };
};

const CategoryProfileLaunches: FCX<CategoryProfileSectionComponentProps> = ({ title, categoryName, data }) => {
    const { id: categoryProfileId } = useParams<{ id: string }>();
    const sectionData = data as LaunchesSectionData | undefined;
    const launches = sectionData ?? EMPTY_CATEGORY_PROFILE_LAUNCHES;
    const lrCategoryIds = sectionData?.lrCategoryIds ?? [];

    const { data: commonData } = useHomeCommonData();

    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();

    const launchesAndRatings = useMemo(
        () => products[AppId.LaunchesAndRatings],
        [products]
    );

    const requestTrial = useCallback(() => {
        toast.info("Request has been sent");
        dispatch(requestTrialByFeature("scores"));
    }, [dispatch]);

    const categoryOptions = useMemo(
        () => commonData.lrCategories
            .filter(category => lrCategoryIds.includes(category.id as number))
            .map(category => ({ id: category.id as number, title: category.title }))
            .sort((a, b) => a.title.localeCompare(b.title)),
        [commonData, lrCategoryIds]
    );

    const buildLaunchesReportUrl = useCallback(
        (targetCategoryId?: number) => http.getQuery('get category profile launches report', {
            categoryProfileId,
            ...(targetCategoryId != null ? { categoryId: targetCategoryId } : {}),
        }),
        [categoryProfileId]
    );

    const handleDownload = useCallback(
        () => http.downloadFileXHRFromUrl(
            'GET',
            buildLaunchesReportUrl(categoryOptions.length === 1 ? categoryOptions[0].id : undefined),
        ),
        [buildLaunchesReportUrl, categoryOptions]
    );

    const handleDownloadCategory = useCallback(
        (targetCategoryId: number) => http.downloadFileXHRFromUrl('GET', buildLaunchesReportUrl(targetCategoryId)),
        [buildLaunchesReportUrl]
    );

    return (
        <div className="CategoryProfile__section-content CategoryProfileLaunches">
            <CategoryProfileSectionTitle
                title={title}
                onDownload={handleDownload}
                downloadOptions={categoryOptions.length > 1 ? categoryOptions : undefined}
                onDownloadOption={handleDownloadCategory}
            />

            <CategoryProfileCard>
                <LaunchesActivityChart activity={launches.activity} categoryName={categoryName} />
            </CategoryProfileCard>

            <div className="CategoryProfileLaunches__rankings">
                <CategoryProfileCard className="CategoryProfileLaunches__ranking">
                    <LaunchesRankingList
                        title={`Most Popular Foods & Flavors: ${categoryName}`}
                        tooltipText={LAUNCHES_FF_TOOLTIP}
                        items={launches.topFoodsFlavors}
                    />
                </CategoryProfileCard>
                <CategoryProfileCard className="CategoryProfileLaunches__ranking">
                    <LaunchesRankingList
                        title={`Most Active Chains: ${categoryName}`}
                        tooltipText={buildLaunchesChainTooltip(categoryName)}
                        items={launches.topChains}
                    />
                </CategoryProfileCard>
            </div>

            <CategoryProfileCard>
                <CategoryProfileSubtitle>
                    {`recently launched items & LTOs: ${categoryName}`}
                </CategoryProfileSubtitle>
                <RecentlyLaunchedCarousel
                    recentItems={launches.recentItems}
                    topItems={launches.topItems}
                />
            </CategoryProfileCard>

            <AccessLink
                url={launchesAndRatings?.url || null}
                text="More Launches & Ratings"
                altText="Upgrade your subscription to reveal new items from top chains."
                hasAccess={!launchesAndRatings?.isDisabled}
                onContactUsClick={requestTrial}
                testId="linkCategoryProfileMoreLaunches"
            />
        </div>
    );
};

export default CategoryProfileLaunches;
